// ** MUI Imports
import React, { useState, useEffect } from "react";
import { createGenericContext } from "./create-generic-context";
import {
  parentDefault,
  studentDefault,
  schoolDefault,
  addressDefault,
  IStudent,
  ISchool,
  IParent,
  ITermsCondition,
  IAddress,
  activeStepDefault,
  IActiveStep,
  getId,
} from "../data/formDefault";
import API from "../../service/api";
import { Typography } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import DuplicateModal from "../../modal/DuplicateModal";

export interface FormContextInterface {
  schoolInfo: ISchool;
  setSchoolInfo: React.Dispatch<React.SetStateAction<ISchool>>;
  studentInfo: IStudent;
  setStudentInfo: React.Dispatch<React.SetStateAction<IStudent>>;
  parentInfo: IParent;
  setParentInfo: React.Dispatch<React.SetStateAction<IParent>>;
  address: IAddress;
  setAddress: React.Dispatch<React.SetStateAction<IAddress>>;
  formSubmitted: boolean;
  addressType: string;
  setAddressType: React.Dispatch<React.SetStateAction<string>>;
  postalCode: string;
  setPostalCode: React.Dispatch<React.SetStateAction<string>>;
  setIsValidParentForm: React.Dispatch<React.SetStateAction<boolean>>;
  setIsValidStudentForm: React.Dispatch<React.SetStateAction<boolean>>;
  termsCondition: ITermsCondition[] | [];
  setTermsCondition: React.Dispatch<React.SetStateAction<ITermsCondition[]>>;
  activeStep: IActiveStep;
  stepChange: (type: string) => void;
  handleFormSubmit: (addressData: any) => void;
  handleSubmitLogActivity: (details: any) => void;
  AddPreApplication: (status: any) => void;
}

interface SignUpFormProps {
  children: React.ReactNode;
}
const [useForm, SignUpFormProvider] = createGenericContext<
  FormContextInterface | undefined
>();

const FormProvider = (props: SignUpFormProps) => {
  const [activeStep, setActiveStep] = useState<IActiveStep>(activeStepDefault);
  const [schoolInfo, setSchoolInfo] = useState<ISchool>(schoolDefault);
  const [studentInfo, setStudentInfo] = useState<IStudent>(studentDefault);
  const [parentInfo, setParentInfo] = useState<IParent>(parentDefault);
  const [termsCondition, setTermsCondition] = useState<ITermsCondition[]>([]);
  const [address, setAddress] = useState<IAddress>(addressDefault);
  const [addressInfo, setAddressInfo] = useState(null);
  const [addressType, setAddressType] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isValidParentForm, setIsValidParentForm] = useState(false);
  const [isValidStudentForm, setIsValidStudentForm] = useState(false);
  const [error, setError] = useState("");
  const [duplicateText, setDuplicateText] = useState("");
  const [loader, setLoader] = useState(false);
  const [preId, setpreId] = useState(0);
  const [openModal, setOpenModal] = React.useState(false);
  const toggleModal = () => setOpenModal((prev) => !prev);

  const stepChange = (type: string) => {
    setActiveStep({
      ...activeStep,
      [type]: true,
    });

    const elementId = getId[type];
    setTimeout(() => {
      const getElementById = document.getElementById(elementId);

      if (getElementById) {
        getElementById.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }
    }, 200);
  };

  useEffect(() => {
    setStudentInfo(studentDefault);
    setParentInfo(parentDefault);
  }, [schoolInfo]);

  const handleFormSubmit = async (addressData: any) => {
    if (isValidParentForm && isValidStudentForm) {
      setLoader(true);
      const value = {
        address: addressData,
        parent: parentInfo,
        student: studentInfo,
        school: schoolInfo,
      };

      try {
        var response = await API.post(
          process.env.REACT_APP_API_ENDPOINT + "Application/Add",
          value
        );

        if (response.successMessage === "Pupil is in waiting list") {
          setDuplicateText(
            `${studentFirstName} is currently already on our waiting list at ${schoolName} so you don’t need to register again. We will be in touch as soon as space becomes available.`
          );
          setOpenModal(true);
          return;
        } else if (response.successMessage === "Pupil is already confirmed") {
          setDuplicateText(
            `${studentFirstName} is already confirmed on our timetable at ${schoolName} so you don’t need to register again.`
          );
          setOpenModal(true);
          return;
        }

        if (
          response.errorMessage !== null &&
          response.errorMessage !== "" &&
          response.errorMessage !== undefined
        ) {
          setError(response.errorMessage);

          setLoader(false);
        } else {
          setFormSubmitted(true);
          setLoader(false);
          setError("");
          window.parent.postMessage("signup-form-submitted", "*");
        }
      } catch (e) {
        console.log("e", e);
        setLoader(false);
        setError(
          "There was an error while saving application form, Please try again."
        );
      }
    }
  };

  const handleSubmitLogActivity = ({
    ActivityType,
    SchoolId,
    SchoolName,
  }: any) => {
    const value = {
      Source: "Application-Form-v2",
      ActivityType: ActivityType,
      SchoolName: SchoolName ?? schoolInfo.label,
      SchoolId: SchoolId ?? Number(schoolInfo.id),
      Referer: "",
    };
    try {
      API.post(process.env.REACT_APP_API_ENDPOINT + "SignUpForm", value);
    } catch (e) {}
  };

  const AddPreApplication = ({
    firstName,
    lastName,
    schoolId,
    ParentFirstname,
    ParentLastname,
    email,
    email2,
    mobile,
    mobile2,
    id,
  }: any) => {
    const value = {
      id: preId,
      firstName: firstName,
      lastName: lastName,
      schoolId: schoolId,
      parentFirstname: ParentFirstname,
      parentLastname: ParentLastname,
      email: email,
      email2: email2,
      mobile: mobile,
      mobile2: mobile2,
    };
    try {
      var data = API.post(
        process.env.REACT_APP_API_ENDPOINT + "Application/AddPreApplication",
        value
      ).then((id) => {
        setpreId(id);
      });
    } catch (e) {}
  };

  const contextValue = {
    schoolInfo,
    setSchoolInfo,
    activeStep,
    stepChange,
    addressType,
    setAddressType,
    postalCode,
    setPostalCode,
    studentInfo,
    setStudentInfo,
    parentInfo,
    setParentInfo,
    addressInfo,
    setAddressInfo,
    handleFormSubmit,
    formSubmitted,
    setFormSubmitted,
    termsCondition,
    setTermsCondition,
    address,
    setAddress,
    handleSubmitLogActivity,
    setIsValidStudentForm,
    setIsValidParentForm,
    AddPreApplication,
  };

  const studentFirstName = studentInfo.studentFirstName;

  const schoolName = schoolInfo.label;
  return (
    <>
      <SignUpFormProvider value={contextValue}>
        {props.children}
      </SignUpFormProvider>
      <div>
        <Typography
          display={
            error !== "" && error !== null && error !== undefined
              ? "block"
              : "none"
          }
          color={"red"}
        >
          {error}
        </Typography>
      </div>
      <Backdrop
        sx={{ color: "#000", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
        onClick={handleFormSubmit}
      >
        <CircularProgress color="inherit" />
        {openModal && (
          <DuplicateModal
            openModal={openModal}
            toggleModal={toggleModal}
            description={duplicateText}
          />
        )}
      </Backdrop>
    </>
  );
};

export { FormProvider, useForm };
